import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { Heading, IconList as SparkyIconList, IconLoader, Stack } from '@eneco/sparky';
import { ChevronRightIcon } from '@eneco/sparky/icons';
import type { IconProps } from '@eneco/sparky/types';

import type { IconListContent } from './icon-list-content.types';

/**
 * `IconList` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/IconList/IconList.tsx`): an
 * optional heading above a list of icon + text rows, falling back to a chevron for an unresolvable icon.
 *
 * See `icon-list-content.types.ts` — the generated Sanity schema has no `items` field at all yet, so
 * this only ever renders the title on `?_cms=sanity` today. `icon` also needs its Sitecore droplist
 * shape (`{name}`) read out, alongside the plain string a Sanity field would hold.
 */
const IconList: FC = () => {
  const content = useContent<IconListContent>();
  const items = content.items ?? [];

  return (
    <Stack direction="column" gap="2">
      {content.title && (
        <Stack.Item>
          <Heading size="3XS" as="h2">
            {content.title}
          </Heading>
        </Stack.Item>
      )}
      <Stack.Item>
        <SparkyIconList>
          {items.map((item, index) => {
            if (!item.text) return null;

            const iconName = typeof item.icon === 'string' ? item.icon : item.icon?.name;

            return (
              <SparkyIconList.Item
                key={item._key ?? String(index)}
                icon={<IconLoader iconName={iconName} fallbackIcon={<ChevronRightIcon />} color={(item.color as IconProps['color']) ?? 'currentColor'} />}>
                {item.text}
              </SparkyIconList.Item>
            );
          })}
        </SparkyIconList>
      </Stack.Item>
    </Stack>
  );
};

export default IconList;
